import React, { useState } from "react";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box, Button, Flex, FormLabel, HStack, Input, Spinner, Text, useToast, VStack } from "@chakra-ui/react";
import { FaGripVertical } from "react-icons/fa";
import { Trash } from "@phosphor-icons/react";
import { useGetStagesOfRound } from "hooks/query";
import { showErrorToast } from "utils";

import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { colors } from "theme/foundations/colors";
import { prettyPrintCodeName } from "utils/text-utils";
import IssbAPI from "api/issb/issb";
import DevOnly from "context/Auth/DevOnly";
import useAirUserRoles from "utils/AuthUtils";

/**See WorkflowStageType.java */
const DESCRIPTION_FOR_STAGE_TYPE = {
  START: "Preparation 1: Start",
  AI_QUEUE: "Preparation 2: Queued for AI analysis",
  EXPERT_REVIEW:
    "Preparation 3: The AI has tried to make an answer. It may or may not have succeeded. Ready for expert review.",
  PENDING: "Ready for general editing, but no answer has been written",
  IN_PROGRESS: "An answer is in progress",
  INTERNAL_REVIEW: "Internal checks before Audit",
  AUDIT: "An Audit - either internal or external",
  COMPLETE: "Passed audit(s). Ready for disclosure",
  // EXCLUDED: "Flag that an answer is not required and remove the question from the workflow" HACK: not offered as a stage type
};

// Stage name (labels) -- defaults, which can be edited:
//  /**
//      * Preparation 1: Initial state (in preparation with the round controller, and not yet open for general editing)
//      */
//  START("In Preparation"),
//  /**
//   * Preparation 2: Queued for AI analysis
//   */
//  AI_QUEUE("Queued"),
//  /**
//   * Preparation 3: The AI has tried to make an answer. It may or may not have succeeded. Ready for expert review.
//   */
//  EXPERT_REVIEW("Ready for Review"),
//  /**
//   * Ready for general editing IF the round has been open, but no answer has been written.
//   */
//  PENDING(null),
//  /**
//   * An answer is in progress -- work has been done on the question.
//   */
//  IN_PROGRESS(null),
//  /**
//   * The company's general editors have marked it ready
//   */
//  INTERNAL_REVIEW("Ready for Approval"),
//  /**
//   * An owner has marked it ready for an audit (there could be a few -- which would all use this status)
//   */
//  AUDIT("Ready for Audit"),
//  /**
//   * Passed all audits. Done! Hurrah!
//   * Ready for disclosure
//   */
//  COMPLETE("Ready for Disclosure"),

export const ATTESTATION_STAGES = ["INTERNAL_REVIEW", "AUDIT"];

// mostly done by prettyPrintCodeName, but "AI" -> "Ai" is a mistake
const LABEL_FOR_KEY = {
  AI_QUEUE: "AI Queue",
};

function labeller(key) {
  return LABEL_FOR_KEY[key] || prettyPrintCodeName(key);
}

/**
 * For now, most stages cannot be edited.
 * @param {*} stageType
 * @returns {boolean}
 */
function isLockedStage(stage) {
  // Note: older data may have START as an incorrect stage type, so allow fixing that
  if (stage.stageType === "AUDIT") return false;
  if (stage.stageType === "START" && stage.stageOrder > 0) return false;
  return true;
}

const SortableStageItem = ({ stage, onDelete, setModified }) => {
  const isLocked = isLockedStage(stage);
  // we edit the stages data object directly, so we need a way to trigger a re-render
  const [refresh, setRefresh] = useState(0);

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: stage.localId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  //   // Not allowed to change the stage type (at present). Only custom Audits allowed
  //   const handleChangeStageType = (value) => {
  //     stage.stageType = value;
  //     if (!stage.name) stage.name = labeller(value);
  //     setModified(true);
  //     setRefresh(refresh + 1);
  //   };
  const handleChangeStageName = (value) => {
    stage.name = value;
    setModified(true);
    setRefresh(refresh + 1);
  };

  const handleChangeAttestationComment = (value) => {
    stage.comment = value;
    setModified(true);
    setRefresh(refresh + 1);
  };

  return (
    <VStack
      ref={setNodeRef}
      style={style}
      w="100%"
      p={3}
      borderWidth="1px"
      borderRadius="lg"
      bg={isLocked ? "gray.200" : "gray.50"}
      boxShadow="sm"
    >
      <HStack w="100%">
        {/* Drag Handle */}
        {isLocked ? (
          <Box cursor="not-allowed" w="20px"></Box>
        ) : (
          <Box {...attributes} {...listeners} cursor="grab">
            <FaGripVertical size={20} />
          </Box>
        )}
        <Box ml={2} w="30%" title={DESCRIPTION_FOR_STAGE_TYPE[stage.stageType]}>
          {labeller(stage.stageType)}
        </Box>
        {/* <Select placeholder="Stage Type"
                value={stage.stageType}
                onChange={e => handleChangeStageType(e.target.value)}
                className={isLocked ? "readonly" : ""} >
          {Object.entries(DESCRIPTION_FOR_STAGE_TYPE).map(([key, value]) => (
            <option key={key} value={key} title={value}>{labeller(key)}</option>
          ))}
        </Select> */}
        <Text>Name</Text>
        <Input placeholder="Stage Name" value={stage.name} onChange={(e) => handleChangeStageName(e.target.value)} />
        {/* Delete Button */}
        {!isLocked && (
          <Button
            variant="outline"
            title="Delete stage"
            color={colors.esg_red}
            size={16}
            onClick={() => onDelete(stage.localId)}
          >
            <Trash />
          </Button>
        )}
      </HStack>

      {/* if stage.stageType = "Audit", show attestation comment box */}
      {ATTESTATION_STAGES.includes(stage.stageType) && (
        <VStack w="100%" gap={0}>
          <FormLabel alignSelf="start">Attestation comments</FormLabel>
          <Input
            placeholder="Attestation comments"
            value={stage?.comment}
            onChange={(e) => handleChangeAttestationComment(e.target.value)}
          />
        </VStack>
      )}
      {/* other controls here?? */}
    </VStack>
  );
}; // ./SortableStageItem

const QuestionnaireStages = () => {
  const [stages, setStages] = useState([]);

  const toast = useToast();

  const roundSelector = useSelector((state) => state.issbRound);

  const questionnaireId = roundSelector.selectedRound?.id;

  const { t } = useTranslation("translation", {
    keyPrefix: "issb.stage.setup",
  });

  // get the stages from the server
  const { isLoading: isFetchingStages } = useGetStagesOfRound(questionnaireId, {
    onSuccess: (data) => {
      const currentStages = data.sort((a, b) => a.order - b.order);
      // create local IDs for the stages (so we can sort them alongside freshly created no-database-ID stages)
      currentStages.forEach((stage) => (stage.localId = stage.id));
      setStages(currentStages);
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
    staleTime: 0,
  });

  const { isController, getAirRoles } = useAirUserRoles();

  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));

  // add a new stage, just before the complete stage
  const handleAddStage = () => {
    if (!isController()) {
      console.log("User is not controller, aborting add stage", getAirRoles());
      return;
    }
    const completeStageIndex = stages.findIndex((stage) => stage.stageType === "COMPLETE");
    // Only custom Audits allowed for now
    const auditLabel = stages.filter((stage) => stage.stageType === "AUDIT").length + 1;
    // HACK: temp ID for the new stage, so that equality-by-ID works
    const newStage = { localId: `temp-${Date.now()}`, name: "Audit " + auditLabel, stageType: "AUDIT" };
    let newStages = [];
    if (completeStageIndex === -1) {
      newStage.stageOrder = stages.length;
      newStages = [...stages, newStage];
    } else {
      newStage.stageOrder = completeStageIndex;
      newStages = [...stages.slice(0, completeStageIndex), newStage, ...stages.slice(completeStageIndex)];
    }
    setStages(newStages);
  };

  const handleDeleteStage = (localId) => {
    if (!isController()) {
      console.log("User is not controller, aborting delete stage", getAirRoles());
      return;
    }
    const newStages = stages.filter((stage) => stage.localId !== localId);
    setStages(newStages);
    setModified(true);
  };

  const handleDragEnd = (event) => {
    if (!isController()) {
      console.log("User is not controller, aborting edit stage", getAirRoles());
      return;
    }
    const { active, over } = event;
    if (!over || active === over) return;
    const oldIndex = stages.findIndex((stage) => stage.localId === active.id);
    const newIndex = stages.findIndex((stage) => stage.localId === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      console.error("(abort drag-move) Stage not found", active, over, stages);
      return;
    }
    // check if newIndex is allowed -- eg Complete must be at the end
    if (newIndex === stages.length - 1) {
      console.log("(abort drag-move) Last stage must be Complete");
      return;
    }
    if (isLockedStage(stages[newIndex])) {
      console.log("(abort drag-move) Cannot move locked stage");
      return;
    }
    const reorderedStages = arrayMove(stages, oldIndex, newIndex);
    // set the stage order
    reorderedStages.forEach((stage, index) => {
      stage.stageOrder = index;
    });
    setStages(reorderedStages);
  };

  const [modified, setModified] = useState(false);

  const handleSaveAll = () => {
    if (!isController()) {
      console.log("User is not controller, aborting save all", getAirRoles());
      return;
    }
    IssbAPI.saveStagesOfRound(questionnaireId, stages)
      .then(() => {
        setModified(false);
      })
      // TODO error toast not working??
      .catch((err) =>
        showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message, "save-stages-error")
      );
  };

  if (isFetchingStages) {
    return <Spinner />;
  }
  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        {isController() && (
          <Button onClick={handleAddStage} size="md" variant="secondary">
            {t("buttons.addStage")}
          </Button>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={stages.map((stage) => stage.localId)} strategy={verticalListSortingStrategy}>
            <VStack spacing={4} align="stretch">
              {stages.map((stage) => (
                <SortableStageItem
                  key={stage.localId}
                  stage={stage}
                  onDelete={handleDeleteStage}
                  setModified={setModified}
                />
              ))}
            </VStack>
          </SortableContext>
        </DndContext>

        <Flex justify="end">
          <Button type="submit" size="lg" variant="primary" onClick={() => handleSaveAll()} isDisabled={!modified}>
            {t("buttons.saveAll")}
          </Button>
        </Flex>

        <DevOnly>
          <Text>
            AIR Roles:
            {JSON.stringify(getAirRoles())}
          </Text>
        </DevOnly>
      </VStack>
    </Box>
  );
};

export default QuestionnaireStages;
